import React, { useState, useEffect } from "react";
import { Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useProfile } from "../../../Components/Hooks/UserHooks";

import {
  getReportProject as onGetReportProject,
  getPhases,
} from "../../../slices/thunks";
import { fetchCostReport } from "../../../slices/report/thunk";
import { clearCostReport } from "../../../slices/report/reducer";
import { selectCostReportLoading } from "../../../slices/report/selectors";
import { selectPhases } from "../../../slices/phases/selectors";

import ReportingHeader from "./components/ReportingHeader";
import ViewTabs, { VIEW_OVERVIEW, VIEW_BUDGET, VIEW_REVENUE } from "./components/ViewTabs";
import OverviewView from "./views/OverviewView";
import BudgetView from "./views/BudgetView";
import RevenueView from "./views/RevenueView";
import { SkeletonBudgetView, SkeletonRevenueView } from "./components/ReportingSkeleton";


const ProjectReporting = () => {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const phaseId = searchParams.get("phase");
  const view = location.pathname.includes("/revenue")
    ? VIEW_REVENUE
    : location.pathname.includes("/budget")
      ? VIEW_BUDGET
      : VIEW_OVERVIEW;

  const [project, setProject] = useState({});
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const { userProfile } = useProfile();

  const phases = useSelector(selectPhases);
  const costReportLoading = useSelector(selectCostReportLoading);

  const currentPhase = phaseId
    ? phases.find((p) => String(p.id) === phaseId) || null
    : null;

  useEffect(() => {
    if (!userProfile) return;
    setLoading(true);
    Promise.all([
      dispatch(onGetReportProject(projectId)),
      dispatch(getPhases()),
    ])
      .then(([res]) => {
        setProject(res.payload?.project || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [dispatch, projectId, userProfile]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchCostReport({ projectId, phaseId }));
    return () => {
      dispatch(clearCostReport());
    };
  }, [projectId, phaseId, dispatch]);

  const handlePhaseChange = (id) => {
    if (id) {
      setSearchParams({ phase: id });
    } else {
      setSearchParams({});
    }
  };

  const handleViewChange = (nextView) => {
    const params = phaseId ? `?phase=${phaseId}` : "";
    navigate(`/project-reporting/${projectId}/${nextView}${params}`);
  };

  const handleBlockChange = (blockId) => {
    if (blockId && phaseId) {
      navigate(`/project-reporting/${projectId}/revenue/phase/${phaseId}/block/${blockId}`);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {view === VIEW_REVENUE ? <SkeletonRevenueView /> : <SkeletonBudgetView />}
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <ReportingHeader
          projectName={project.name}
          projectId={projectId}
          phase={currentPhase}
          phases={phases}
          onPhaseChange={handlePhaseChange}
          blocks={view === VIEW_REVENUE && currentPhase ? currentPhase.blocks || [] : []}
          onBlockChange={handleBlockChange}
        />


        <ViewTabs view={view} onChange={handleViewChange} />

        {costReportLoading ? (
          view === VIEW_REVENUE ? <SkeletonRevenueView /> : <SkeletonBudgetView />
        ) : view === VIEW_OVERVIEW ? (
          <OverviewView projectId={projectId} phaseId={phaseId} />
        ) : view === VIEW_BUDGET ? (
          <BudgetView phaseId={phaseId} />
        ) : (
          <RevenueView />
        )}
      </Container>
    </div>
  );
};

export default ProjectReporting;
